"use client";

import { apiGetGuessOrderBook } from "@/api/order";
import {
    ConnectManager,
    createOrderBookEventSubscriptionKey,
    createOrderBookGuessSubscriptionKey,
    OrderBookSubscriptionManager,
    SOCKET_KEY,
} from "@yn/socket";
import { BigMath } from "@yn/utils";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// --- 轻量级类型安全事件发射器 ---
type Listener<T> = ( event: T ) => void;

function createEmitter<T>() {
    const listeners = new Set<Listener<T>>();
    return {
        on( fn: Listener<T> ) {
            listeners.add( fn );
        },
        off( fn: Listener<T> ) {
            listeners.delete( fn );
        },
        emit( event: T ) {
            listeners.forEach( ( fn ) => fn( event ) );
        },
        clear() {
            listeners.clear();
        },
    };
}

// --- Types (Copied/Adapted from orderbook-ws.ts) ---

export enum GuessType {
    Yes = 1,
    No = 2,
}

export enum Direction {
    Buy = 1,
    Sell = 2,
}

type GuessTypeMap<T> = Partial<Record<GuessType, T>>;

interface OrderbookFullSnapshotLevel {
    price: number;
    remainAmount: number;
    guessType: GuessType;
    direction: Direction;
}

interface OrderbookFullSnapshotPayload {
    eventNo: string;
    guessNo: string;
    packageId: number;
    marketId?: string;
    timestamp: number;
    status?: "TRADING" | "PAUSED" | "DELETED" | string;
    levels: OrderbookFullSnapshotLevel[];
}

interface OrderBookSnapshotV2Payload {
    eventNo: string;
    guessNo: string;
    timestamp: number;
    status?: "TRADING" | "PAUSED" | "DELETED" | string;
    yesLevels: Array<{ price: number; remainAmount: number; convertedBuyQuantity?: number }>;
    noLevels: Array<{ price: number; remainAmount: number; convertedBuyQuantity?: number }>;
}

interface MarketPriceMessage {
    eventNo: string;
    guessNo: string;
    packageId: number;
    timestamp: number; // 毫秒
    price: number;
    guessType: GuessType;
}

export interface OrderBookRow {
    price: number;
    totalQuantity: number;
    totalAmount: number;
    orderBookUpdatedAt: number; // 当前价格档位的最新更新时间戳（毫秒）
}

export interface OrderBookSnapshot {
    sellOrders: Array<OrderBookRow>;
    buyOrders?: Array<OrderBookRow>;
}

interface OrderBookFreshnessState {
    snapshotUpdatedAt: number; // 快照基线时间戳，用于未出现过的新价格档位兜底
    prices: Record<string, number>;
}

// 市场价格变化事件类型 - 对外使用简化格式（向后兼容）
export interface MarketPriceChangeEvent {
    guessNo: string;
    yesPrice: number | null; // 简化为数字
    noPrice: number | null; // 简化为数字
    timestamp: number;
}

// 交易金额变化事件类型
export interface TradeAmountChangeEvent {
    eventNo: string;
    guessNo: string;
    tradeAmount: number;
    timestamp: number;
    guessType: GuessType;
}

// 模块级单例 —— 不放入 store state，避免 Immer 代理
const marketPriceEmitter = createEmitter<MarketPriceChangeEvent>();
const tradeAmountEmitter = createEmitter<TradeAmountChangeEvent>();
const snapshotRequests = new Map<string, Promise<void>>();
const pendingEventReleaseTimers = new Map<string, ReturnType<typeof setTimeout>>();
const EVENT_SUBSCRIPTION_RELEASE_GRACE_MS = 300;

type SubscriptionRefCounts = Record<string, number>;
type GuessEventIndex = Record<string, string>;

// Store State - 内部使用新的数据结构
interface EventConnectStateInternal {
    currentEventNo: string | null;
    eventSubscriptionRefs: SubscriptionRefCounts;
    guessSubscriptionRefs: SubscriptionRefCounts;
    trackedEventRefs: SubscriptionRefCounts;
    guessEventIndex: GuessEventIndex;
    listenerAttached: boolean;

    // 市场价格时间戳索引，仅用于 WebSocket 新鲜度判断
    _marketPriceTimestamps: Record<string, number>;

    // 订单簿价格维度新鲜度索引（不对外暴露）
    _orderbookFreshness: Record<string, GuessTypeMap<OrderBookFreshnessState>>;

    // 5. 竞猜订单对象 (存放当前事件中所有竞猜订单信息 - 改名为 orderbookData)
    orderbookData: Record<string, GuessTypeMap<OrderBookSnapshot>>; // guessNo -> betType -> snapshot
    snapshotLoadingStates: Record<string, GuessTypeMap<boolean>>; // guessNo -> betType -> loading

    // Actions
    subscribe: ( eventNo: string ) => void;
    unsubscribe: () => void;
    subscribeEvent: ( eventNo: string ) => void;
    unsubscribeEvent: ( eventNo?: string | null ) => void;
    subscribeGuess: ( eventNo: string, guessNo: string ) => void;
    unsubscribeGuess: ( eventNo: string, guessNo: string ) => void;
    loadSnapshot: ( guessNo: string, opts?: { force?: boolean } ) => Promise<void>;
    initGuesses: ( guesses: any, eventNo?: string | null ) => void;
    // 同步 API 返回的价格到 store
    syncApiPrices: (
        guessNo: string,
        yesMarketPrice: number | null,
        noMarketPrice: number | null,
        timestamp?: number,
    ) => void;

    // Internal Actions (called by socket listener)
    handleMarketPrice: ( message: MarketPriceMessage ) => void;
    handleOrderbookFullSnapshotMessage: ( message: OrderbookFullSnapshotPayload ) => void;
    handleOrderBookSnapshotV2: ( message: OrderBookSnapshotV2Payload ) => void;

    // 价格变化监听方法
    onMarketPriceChange: ( callback: ( event: MarketPriceChangeEvent ) => void ) => () => void;
    // 交易金额变化监听方法
    onTradeAmountChange: ( callback: ( event: TradeAmountChangeEvent ) => void ) => () => void;

    // Handler for trade amount messages
    handleTradeAmount: ( message: TradeAmountMessage ) => void;

    // Helper to clear data
    clearData: () => void;
}

// 导出兼容型的 guesses 接口（供外部使用，只包含价格数字）
export interface CompatibleGuessData {
    yesPrice: number | null;
    noPrice: number | null;
}

type MarketPriceState = {
    guesses: Record<string, CompatibleGuessData>;
    _marketPriceTimestamps: Record<string, number>;
};

type MarketPriceUpdate = {
    guessNo: string;
    yesPrice: number | null | undefined;
    noPrice: number | null | undefined;
    timestamp?: number;
};

const CONVERTED_BUY_PRICE_TOTAL = 100;

const normalizeTimestamp = ( timestamp: unknown ): number | undefined => {
    if ( timestamp === null || timestamp === undefined || timestamp === "" ) {
        return undefined;
    }

    const normalizedTimestamp = Number( timestamp );
    return Number.isFinite( normalizedTimestamp ) ? normalizedTimestamp : undefined;
};

const applyMarketPriceUpdate = (
    state: MarketPriceState,
    { guessNo, yesPrice, noPrice, timestamp }: MarketPriceUpdate,
    options?: { rejectEqualTimestamp?: boolean },
) => {
    const hasYesPrice = yesPrice !== null && yesPrice !== undefined;
    const hasNoPrice = noPrice !== null && noPrice !== undefined;
    if ( !hasYesPrice && !hasNoPrice ) {
        return false;
    }

    const currentTimestamp = state._marketPriceTimestamps[guessNo] ?? 0;
    if ( timestamp !== undefined ) {
        const shouldReject = options?.rejectEqualTimestamp
            ? timestamp <= currentTimestamp
            : timestamp < currentTimestamp;
        if ( shouldReject ) {
            return false;
        }

        state._marketPriceTimestamps[guessNo] = timestamp;
    } else if ( currentTimestamp > 0 ) {
        return false;
    }

    const currentGuess = state.guesses[guessNo] || { yesPrice: null, noPrice: null };
    state.guesses[guessNo] = {
        yesPrice: hasYesPrice ? yesPrice : currentGuess.yesPrice,
        noPrice: hasNoPrice ? noPrice : currentGuess.noPrice,
    };

    return true;
};

// Store State - 导出时使用兼容型接口
interface EventConnectState extends EventConnectStateInternal {
    // 对外暴露：简化的价格数据（只有价格数字，用于向后兼容）
    guesses: Record<string, CompatibleGuessData>;
}

const createOrderBookRow = ( price: number, remainAmount: number, orderBookUpdatedAt: number ): OrderBookRow => ( {
    price,
    totalQuantity: remainAmount,
    totalAmount: BigMath.times( price, remainAmount ),
    orderBookUpdatedAt,
} );

const createConvertedBuyOrderBookRow = (
    sellPrice: number,
    convertedBuyQuantity: unknown,
    orderBookUpdatedAt: number,
): OrderBookRow | null => {
    const buyPrice = BigMath.minus( CONVERTED_BUY_PRICE_TOTAL, sellPrice );
    const buyQuantity = Number( convertedBuyQuantity );

    if (
        !Number.isFinite( sellPrice )
        || !Number.isFinite( buyPrice )
        || !Number.isFinite( buyQuantity )
        || !BigMath.gt( buyPrice, 0 )
        || !BigMath.gt( buyQuantity, 0 )
    ) {
        return null;
    }

    return createOrderBookRow( buyPrice, buyQuantity, orderBookUpdatedAt );
};

const createEmptyOrderBookByGuessType = (): Record<GuessType, OrderBookSnapshot> => ( {
    [GuessType.Yes]: { sellOrders: [], buyOrders: [] },
    [GuessType.No]: { sellOrders: [], buyOrders: [] },
} );

const sortOrderBookRows = ( orders: OrderBookRow[] ) =>
    orders.sort( ( a, b ) => {
        if ( BigMath.gt( a.price, b.price ) ) return 1;
        if ( BigMath.lt( a.price, b.price ) ) return -1;
        return 0;
    } );

const toOrderBookPriceKey = ( price: number ) => String( Number( price ) );

const ensureOrderBookFreshnessState = (
    freshnessState: Record<string, GuessTypeMap<OrderBookFreshnessState>>,
    guessNo: string,
    betType: GuessType,
): OrderBookFreshnessState => {
    if ( !freshnessState[guessNo] ) {
        freshnessState[guessNo] = {};
    }

    if ( !freshnessState[guessNo][betType] ) {
        freshnessState[guessNo][betType] = { snapshotUpdatedAt: 0, prices: {} };
    }

    return freshnessState[guessNo][betType];
};

const seedOrderBookFreshness = (
    freshness: OrderBookFreshnessState,
    orderBookUpdatedAt: number,
    sellOrders: OrderBookRow[],
) => {
    freshness.snapshotUpdatedAt = Number( orderBookUpdatedAt || 0 );
    freshness.prices = Object.fromEntries(
        sellOrders.map( ( order ) => [ toOrderBookPriceKey( order.price ), order.orderBookUpdatedAt ] ),
    );
};

const getRefCount = ( refs: SubscriptionRefCounts, key: string ) => refs[key] ?? 0;

const setRefCount = ( refs: SubscriptionRefCounts, key: string, count: number ) => {
    if ( count > 0 ) {
        refs[key] = count;
        return;
    }

    delete refs[key];
};

const getTrackedEventNoList = ( trackedEventRefs: SubscriptionRefCounts ) => Object.keys( trackedEventRefs );

const isEventTracked = ( trackedEventRefs: SubscriptionRefCounts, eventNo?: string | null ) => {
    if ( !eventNo ) {
        return false;
    }

    return getRefCount( trackedEventRefs, eventNo ) > 0;
};

const ensureSocketListenerAttached = ( draft: EventConnectStateInternal ) => {
    if ( draft.listenerAttached ) {
        return;
    }

    getOrderBookConnectManager().addEventListeners( socketListener );
    draft.listenerAttached = true;
};

const detachSocketListenerIfIdle = ( draft: EventConnectStateInternal ) => {
    if ( !draft.listenerAttached || getTrackedEventNoList( draft.trackedEventRefs ).length > 0 ) {
        return;
    }

    getOrderBookConnectManager().removeEventListeners( socketListener );
    draft.listenerAttached = false;
};

const getOrderBookConnectManager = () => {
    const manager = ConnectManager.getInstance( SOCKET_KEY.ORDERBOOK );
    if ( !manager.subscriptionManager ) {
        manager.setSubscriptionManager( new OrderBookSubscriptionManager() );
    }
    return manager;
};

const clearGuessOrderBookData = ( draft: EventConnectStateInternal, guessNo?: string | null ) => {
    if ( !guessNo ) {
        return;
    }

    delete draft._orderbookFreshness[guessNo];
    delete draft.orderbookData[guessNo];
    delete draft.snapshotLoadingStates[guessNo];
};

const clearEventScopedData = (
    draft: EventConnectStateInternal & { guesses: Record<string, CompatibleGuessData> },
    eventNo: string,
) => {
    Object.entries( draft.guessEventIndex ).forEach( ( [ guessNo, indexedEventNo ] ) => {
        if ( indexedEventNo !== eventNo ) {
            return;
        }

        delete draft.guessEventIndex[guessNo];
        delete draft.guesses[guessNo];
        delete draft._marketPriceTimestamps[guessNo];
        clearGuessOrderBookData( draft, guessNo );
    } );
};

const registerGuessEvent = ( draft: EventConnectStateInternal, eventNo: string, guessNo?: string | null ) => {
    if ( !eventNo || !guessNo ) {
        return;
    }

    draft.guessEventIndex[guessNo] = eventNo;
};

const applyOrderbookFullSnapshot = ( draft: EventConnectStateInternal, payload: OrderbookFullSnapshotPayload ) => {
    const { eventNo, guessNo } = payload;
    const normalizedTimestamp = normalizeTimestamp( payload.timestamp ) ?? 0;
    const currentFreshness = draft._orderbookFreshness[guessNo];
    const localTimestamp = Math.max(
        currentFreshness?.[GuessType.Yes]?.snapshotUpdatedAt ?? 0,
        currentFreshness?.[GuessType.No]?.snapshotUpdatedAt ?? 0,
    );

    if ( normalizedTimestamp < localTimestamp ) {
        return;
    }

    registerGuessEvent( draft, eventNo, guessNo );

    const nextOrderbook = createEmptyOrderBookByGuessType();
    const levels = Array.isArray( payload.levels ) ? payload.levels : [];

    levels.forEach( ( level ) => {
        if (
            level.direction !== Direction.Sell
            || ( level.guessType !== GuessType.Yes && level.guessType !== GuessType.No )
        ) {
            return;
        }

        const price = Number( level.price );
        const remainAmount = Number( level.remainAmount );
        if ( !Number.isFinite( price ) || !Number.isFinite( remainAmount ) || !BigMath.gt( remainAmount, 0 ) ) {
            return;
        }

        nextOrderbook[level.guessType].sellOrders.push(
            createOrderBookRow( price, remainAmount, normalizedTimestamp ),
        );
    } );

    sortOrderBookRows( nextOrderbook[GuessType.Yes].sellOrders );
    sortOrderBookRows( nextOrderbook[GuessType.No].sellOrders );

    draft.orderbookData[guessNo] = nextOrderbook;

    const yesFreshness = ensureOrderBookFreshnessState( draft._orderbookFreshness, guessNo, GuessType.Yes );
    const noFreshness = ensureOrderBookFreshnessState( draft._orderbookFreshness, guessNo, GuessType.No );
    seedOrderBookFreshness( yesFreshness, normalizedTimestamp, nextOrderbook[GuessType.Yes].sellOrders );
    seedOrderBookFreshness( noFreshness, normalizedTimestamp, nextOrderbook[GuessType.No].sellOrders );
};

const applyOrderBookSnapshotV2 = ( draft: EventConnectStateInternal, payload: OrderBookSnapshotV2Payload ) => {
    const { eventNo, guessNo, timestamp, status } = payload;
    const normalizedTimestamp = normalizeTimestamp( timestamp ) ?? 0;
    const currentFreshness = draft._orderbookFreshness[guessNo];
    const localTimestamp = Math.max(
        currentFreshness?.[GuessType.Yes]?.snapshotUpdatedAt ?? 0,
        currentFreshness?.[GuessType.No]?.snapshotUpdatedAt ?? 0,
    );

    if ( normalizedTimestamp < localTimestamp ) {
        return;
    }

    registerGuessEvent( draft, eventNo, guessNo );

    if ( status === "DELETED" ) {
        clearGuessOrderBookData( draft, guessNo );
        return;
    }

    const nextOrderbook = createEmptyOrderBookByGuessType();

    payload.yesLevels.forEach( ( level ) => {
        const price = Number( level.price );
        const remainAmount = Number( level.remainAmount );
        if ( Number.isFinite( price ) && Number.isFinite( remainAmount ) && BigMath.gt( remainAmount, 0 ) ) {
            nextOrderbook[GuessType.Yes].sellOrders.push(
                createOrderBookRow( price, remainAmount, normalizedTimestamp ),
            );
        }

        const buyOrder = createConvertedBuyOrderBookRow( price, level.convertedBuyQuantity, normalizedTimestamp );
        if ( buyOrder ) {
            // Yes 卖单折算后是 No 买单。
            nextOrderbook[GuessType.No].buyOrders?.push( buyOrder );
        }
    } );

    payload.noLevels.forEach( ( level ) => {
        const price = Number( level.price );
        const remainAmount = Number( level.remainAmount );
        if ( Number.isFinite( price ) && Number.isFinite( remainAmount ) && BigMath.gt( remainAmount, 0 ) ) {
            nextOrderbook[GuessType.No].sellOrders.push(
                createOrderBookRow( price, remainAmount, normalizedTimestamp ),
            );
        }

        const buyOrder = createConvertedBuyOrderBookRow( price, level.convertedBuyQuantity, normalizedTimestamp );
        if ( buyOrder ) {
            // No 卖单折算后是 Yes 买单。
            nextOrderbook[GuessType.Yes].buyOrders?.push( buyOrder );
        }
    } );

    sortOrderBookRows( nextOrderbook[GuessType.Yes].sellOrders );
    sortOrderBookRows( nextOrderbook[GuessType.No].sellOrders );
    sortOrderBookRows( nextOrderbook[GuessType.Yes].buyOrders || [] );
    sortOrderBookRows( nextOrderbook[GuessType.No].buyOrders || [] );

    draft.orderbookData[guessNo] = nextOrderbook;

    const yesFreshness = ensureOrderBookFreshnessState( draft._orderbookFreshness, guessNo, GuessType.Yes );
    const noFreshness = ensureOrderBookFreshnessState( draft._orderbookFreshness, guessNo, GuessType.No );
    seedOrderBookFreshness( yesFreshness, normalizedTimestamp, nextOrderbook[GuessType.Yes].sellOrders );
    seedOrderBookFreshness( noFreshness, normalizedTimestamp, nextOrderbook[GuessType.No].sellOrders );
};

/**
 * @description 新的事件监听
 *
 * 使用：
 * import { useEventConnect } from "@/store/eventConnect";
 *
 * 监听
 * const eventConnect = useEventConnect();
 *
 * eventConnect.subscribe(eventNo);
 *
 * 取消监听
 * eventConnect.unsubscribe();
 *
 *  初始化竞猜数据
 * eventConnect.initGuesses(guesses);
 *
 *  加载订单快照 其实就是初始化数据
 * eventConnect.loadSnapshot(guessNo);
 *
 * 之后什么都不用管 拿竞猜数据 走guesses[guessNo]
 *
 * 拿订单簿数据 走orderbookData[guessNo]
 */
export const useEventConnect = create<EventConnectState>()( devtools( immer( ( set, get ) => ( {
    currentEventNo: null,
    eventSubscriptionRefs: {},
    guessSubscriptionRefs: {},
    trackedEventRefs: {},
    guessEventIndex: {},
    listenerAttached: false,
    guesses: {}, // 对外暴露的兼容型数据
    _marketPriceTimestamps: {},
    _orderbookFreshness: {},
    orderbookData: {},
    snapshotLoadingStates: {},

    subscribe: ( eventNo: string ) => {
        get().subscribeEvent( eventNo );
    },

    unsubscribe: () => {
        get().unsubscribeEvent( get().currentEventNo );
    },

    subscribeEvent: ( eventNo: string ) => {
        if ( !eventNo ) return;

        const subscriptionKey = createOrderBookEventSubscriptionKey( eventNo );
        const state = get();
        const currentRefCount = getRefCount( state.eventSubscriptionRefs, eventNo );
        const hasPendingRelease = pendingEventReleaseTimers.has( eventNo );

        if ( hasPendingRelease ) {
            clearTimeout( pendingEventReleaseTimers.get( eventNo )! );
            pendingEventReleaseTimers.delete( eventNo );
        }

        console.log( "[EventConnect] Subscribing to event:", eventNo );

        if ( currentRefCount === 0 && !hasPendingRelease ) {
            getOrderBookConnectManager().subscribeEvent( subscriptionKey );
        }

        set( ( draft ) => {
            setRefCount( draft.eventSubscriptionRefs, eventNo, currentRefCount + 1 );
            if ( !hasPendingRelease ) {
                setRefCount( draft.trackedEventRefs, eventNo, getRefCount( draft.trackedEventRefs, eventNo ) + 1 );
            }
            draft.currentEventNo = eventNo;
            ensureSocketListenerAttached( draft );
        } );
    },

    unsubscribeEvent: ( eventNo?: string | null ) => {
        const targetEventNo = eventNo || get().currentEventNo;

        if ( !targetEventNo ) {
            return;
        }

        const state = get();
        const currentRefCount = getRefCount( state.eventSubscriptionRefs, targetEventNo );

        if ( currentRefCount === 0 ) {
            return;
        }

        console.log( "[EventConnect] Unsubscribing from event:", targetEventNo );

        set( ( draft ) => {
            setRefCount( draft.eventSubscriptionRefs, targetEventNo, currentRefCount - 1 );
            if ( currentRefCount > 1 ) {
                setRefCount(
                    draft.trackedEventRefs,
                    targetEventNo,
                    getRefCount( draft.trackedEventRefs, targetEventNo ) - 1,
                );

                if ( !isEventTracked( draft.trackedEventRefs, targetEventNo ) ) {
                    clearEventScopedData( draft, targetEventNo );
                }
            }

            draft.currentEventNo = getTrackedEventNoList( draft.trackedEventRefs )[0] ?? null;
            if ( currentRefCount > 1 ) {
                detachSocketListenerIfIdle( draft );
            }
        } );

        if ( currentRefCount !== 1 ) {
            return;
        }

        const existingTimer = pendingEventReleaseTimers.get( targetEventNo );
        if ( existingTimer ) {
            clearTimeout( existingTimer );
        }

        pendingEventReleaseTimers.set(
            targetEventNo,
            setTimeout( () => {
                pendingEventReleaseTimers.delete( targetEventNo );

                const latestState = get();
                if ( getRefCount( latestState.eventSubscriptionRefs, targetEventNo ) > 0 ) {
                    return;
                }

                getOrderBookConnectManager().unsubscribeEvent( createOrderBookEventSubscriptionKey( targetEventNo ) );

                set( ( draft ) => {
                    setRefCount(
                        draft.trackedEventRefs,
                        targetEventNo,
                        getRefCount( draft.trackedEventRefs, targetEventNo ) - 1,
                    );

                    if ( !isEventTracked( draft.trackedEventRefs, targetEventNo ) ) {
                        clearEventScopedData( draft, targetEventNo );
                    }

                    draft.currentEventNo = getTrackedEventNoList( draft.trackedEventRefs )[0] ?? null;
                    detachSocketListenerIfIdle( draft );
                } );
            }, EVENT_SUBSCRIPTION_RELEASE_GRACE_MS ),
        );
    },

    subscribeGuess: ( eventNo: string, guessNo: string ) => {
        if ( !eventNo || !guessNo ) {
            return;
        }

        const guessSubscriptionKey = createOrderBookGuessSubscriptionKey( eventNo, guessNo );
        const state = get();
        const currentRefCount = getRefCount( state.guessSubscriptionRefs, guessSubscriptionKey );

        if ( currentRefCount === 0 ) {
            getOrderBookConnectManager().subscribeEvent( guessSubscriptionKey );
        }

        set( ( draft ) => {
            setRefCount( draft.guessSubscriptionRefs, guessSubscriptionKey, currentRefCount + 1 );
            setRefCount( draft.trackedEventRefs, eventNo, getRefCount( draft.trackedEventRefs, eventNo ) + 1 );
            registerGuessEvent( draft, eventNo, guessNo );
            draft.currentEventNo = eventNo;
            ensureSocketListenerAttached( draft );
        } );
    },

    unsubscribeGuess: ( eventNo: string, guessNo: string ) => {
        if ( !eventNo || !guessNo ) {
            return;
        }

        const guessSubscriptionKey = createOrderBookGuessSubscriptionKey( eventNo, guessNo );
        const state = get();
        const currentRefCount = getRefCount( state.guessSubscriptionRefs, guessSubscriptionKey );

        if ( currentRefCount === 0 ) {
            return;
        }

        console.log( "[EventConnect] Unsubscribing from guess:", { eventNo, guessNo } );

        if ( currentRefCount === 1 ) {
            getOrderBookConnectManager().unsubscribeEvent( guessSubscriptionKey );
        }

        set( ( draft ) => {
            setRefCount( draft.guessSubscriptionRefs, guessSubscriptionKey, currentRefCount - 1 );
            setRefCount( draft.trackedEventRefs, eventNo, getRefCount( draft.trackedEventRefs, eventNo ) - 1 );

            if ( currentRefCount === 1 ) {
                clearGuessOrderBookData( draft, guessNo );
            }

            if ( !isEventTracked( draft.trackedEventRefs, eventNo ) ) {
                clearEventScopedData( draft, eventNo );
            }

            draft.currentEventNo = getTrackedEventNoList( draft.trackedEventRefs )[0] ?? null;
            detachSocketListenerIfIdle( draft );
        } );
    },

    loadSnapshot: async ( guessNo: string, opts?: { force?: boolean } ) => {
        const existingRequest = snapshotRequests.get( guessNo );
        if ( existingRequest ) {
            return existingRequest;
        }
        if ( get().orderbookData[guessNo] && !opts?.force ) {
            return void 0;
        }
        const betTypes: GuessType[] = [ GuessType.Yes, GuessType.No ];

        const trackedRequest = ( async () => {
            set( ( draft ) => {
                if ( !draft.snapshotLoadingStates[guessNo] ) {
                    draft.snapshotLoadingStates[guessNo] = {};
                }
                betTypes.forEach( ( t ) => ( draft.snapshotLoadingStates[guessNo][t] = true ) );
            } );

            try {
                console.log( "[EventConnect] Loading snapshot:", { guessNo } );
                const [ isOk, response ] = await apiGetGuessOrderBook( { guessNo } );

                if ( isOk && response?.data ) {
                    const snapshotData = response.data;
                    const yesOrderBookUpdatedAt = Number(
                        snapshotData.guessTypeUpdateTimes?.[String( GuessType.Yes )]
                            || snapshotData.orderBookUpdatedAt
                            || 0,
                    );
                    const noOrderBookUpdatedAt = Number(
                        snapshotData.guessTypeUpdateTimes?.[String( GuessType.No )]
                            || snapshotData.orderBookUpdatedAt
                            || 0,
                    );
                    const mapOrders = ( orders: any[], orderBookUpdatedAt: number ) => {
                        const sellOrders: OrderBookRow[] = [];
                        const convertedBuyOrders: OrderBookRow[] = [];

                        ( orders || [] ).forEach( ( order: any ) => {
                            const price = Number( order.price );
                            const totalQuantity = Number( order.totalQuantity );

                            if (
                                Number.isFinite( price )
                                && Number.isFinite( totalQuantity )
                                && BigMath.gt( totalQuantity, 0 )
                            ) {
                                sellOrders.push(
                                    createOrderBookRow( price, totalQuantity, Number( orderBookUpdatedAt ) ),
                                );
                            }

                            const buyOrder = createConvertedBuyOrderBookRow(
                                price,
                                order.convertedBuyQuantity,
                                Number( orderBookUpdatedAt ),
                            );
                            if ( buyOrder ) {
                                convertedBuyOrders.push( buyOrder );
                            }
                        } );

                        return {
                            sellOrders: sortOrderBookRows( sellOrders ),
                            convertedBuyOrders: sortOrderBookRows( convertedBuyOrders ),
                        };
                    };

                    set( ( draft ) => {
                        const currentFreshness = draft._orderbookFreshness[guessNo];
                        const localTimestamp = Math.max(
                            currentFreshness?.[GuessType.Yes]?.snapshotUpdatedAt ?? 0,
                            currentFreshness?.[GuessType.No]?.snapshotUpdatedAt ?? 0,
                        );
                        const apiTimestamp = Math.max( yesOrderBookUpdatedAt, noOrderBookUpdatedAt );
                        if ( apiTimestamp < localTimestamp ) {
                            return;
                        }

                        const yesOrderbook = mapOrders( snapshotData.yesOrders, yesOrderBookUpdatedAt );
                        const noOrderbook = mapOrders( snapshotData.noOrders, noOrderBookUpdatedAt );
                        // API 先按来源方向拆单，最终写入时把 converted 买单放到相反方向。
                        const nextOrderbook = {
                            [GuessType.Yes]: {
                                sellOrders: yesOrderbook.sellOrders,
                                buyOrders: noOrderbook.convertedBuyOrders,
                            },
                            [GuessType.No]: {
                                sellOrders: noOrderbook.sellOrders,
                                buyOrders: yesOrderbook.convertedBuyOrders,
                            },
                        };

                        draft.orderbookData[guessNo] = nextOrderbook;

                        const yesFreshness = ensureOrderBookFreshnessState(
                            draft._orderbookFreshness,
                            guessNo,
                            GuessType.Yes,
                        );
                        const noFreshness = ensureOrderBookFreshnessState(
                            draft._orderbookFreshness,
                            guessNo,
                            GuessType.No,
                        );
                        seedOrderBookFreshness( yesFreshness, yesOrderBookUpdatedAt, yesOrderbook.sellOrders );
                        seedOrderBookFreshness( noFreshness, noOrderBookUpdatedAt, noOrderbook.sellOrders );
                    } );
                    console.log( "[EventConnect] Snapshot loaded:", { guessNo, betTypes } );
                } else {
                    console.error( "[EventConnect] Failed to load snapshot:", { guessNo } );
                }
            } catch ( error ) {
                console.error( "[EventConnect] Error loading snapshots:", error, { guessNo } );
            } finally {
                set( ( draft ) => {
                    delete draft.snapshotLoadingStates[guessNo];
                } );
            }
        } )();

        snapshotRequests.set( guessNo, trackedRequest );
        trackedRequest.finally( () => {
            if ( snapshotRequests.get( guessNo ) === trackedRequest ) {
                snapshotRequests.delete( guessNo );
            }
        } );
        return trackedRequest;
    },

    // 同步 API 返回的价格到 store，仅用于初始化或使用明确的 API 时间戳刷新
    syncApiPrices: (
        guessNo: string,
        yesMarketPrice: number | null,
        noMarketPrice: number | null,
        timestamp?: number,
    ) => {
        set( ( draft ) => {
            applyMarketPriceUpdate( draft, {
                guessNo,
                yesPrice: yesMarketPrice,
                noPrice: noMarketPrice,
                timestamp: normalizeTimestamp( timestamp ),
            } );
        } );
    },

    clearData: () => {
        pendingEventReleaseTimers.forEach( ( timer ) => clearTimeout( timer ) );
        pendingEventReleaseTimers.clear();

        set( ( draft ) => {
            draft.currentEventNo = null;
            draft.eventSubscriptionRefs = {};
            draft.guessSubscriptionRefs = {};
            draft.trackedEventRefs = {};
            draft.guessEventIndex = {};
            draft.guesses = {};
            draft._marketPriceTimestamps = {};
            draft._orderbookFreshness = {};
            draft.orderbookData = {};
            draft.snapshotLoadingStates = {};
            detachSocketListenerIfIdle( draft );
        } );
    },

    onMarketPriceChange: ( callback: ( event: MarketPriceChangeEvent ) => void ) => {
        marketPriceEmitter.on( callback );
        return () => {
            marketPriceEmitter.off( callback );
        };
    },

    onTradeAmountChange: ( callback: ( event: TradeAmountChangeEvent ) => void ) => {
        tradeAmountEmitter.on( callback );
        return () => {
            tradeAmountEmitter.off( callback );
        };
    },

    handleMarketPrice: ( message: MarketPriceMessage ) => {
        const state = get();
        if ( !isEventTracked( state.trackedEventRefs, message.eventNo ) ) {
            return;
        }

        const { guessNo, price, guessType, timestamp } = message;
        const yesPrice = guessType === GuessType.Yes ? price : BigMath.minus( 100, price );
        const noPrice = guessType === GuessType.Yes ? BigMath.minus( 100, price ) : price;

        // 用于在 set 回调外 emit 事件
        let emitPayload: MarketPriceChangeEvent | null = null;

        set( ( draft ) => {
            registerGuessEvent( draft, message.eventNo, guessNo );
            const updated = applyMarketPriceUpdate( draft, {
                guessNo,
                yesPrice,
                noPrice,
                timestamp: normalizeTimestamp( timestamp ),
            }, { rejectEqualTimestamp: true } );

            if ( !updated ) {
                return;
            }

            emitPayload = { guessNo, yesPrice, noPrice, timestamp };
        } );

        // 在 set 回调外 emit，确保监听者通过 getState() 能拿到最新已提交的 state
        if ( emitPayload ) {
            marketPriceEmitter.emit( emitPayload );
        }
    },

    handleOrderbookFullSnapshotMessage: ( message: OrderbookFullSnapshotPayload ) => {
        const state = get();
        if ( !isEventTracked( state.trackedEventRefs, message.eventNo ) ) {
            return;
        }

        set( ( draft ) => {
            applyOrderbookFullSnapshot( draft, message );
        } );
    },

    handleOrderBookSnapshotV2: ( message: OrderBookSnapshotV2Payload ) => {
        const state = get();
        if ( !isEventTracked( state.trackedEventRefs, message.eventNo ) ) {
            return;
        }

        set( ( draft ) => {
            applyOrderBookSnapshotV2( draft, message );
        } );
    },

    handleTradeAmount: ( message: TradeAmountMessage ) => {
        const state = get();
        if ( !isEventTracked( state.trackedEventRefs, message.eventNo ) ) {
            return;
        }

        // 发射交易金额变化事件
        tradeAmountEmitter.emit( {
            eventNo: message.eventNo,
            guessNo: message.guessNo,
            tradeAmount: message.tradeAmount,
            timestamp: message.timestamp,
            guessType: message.guessType,
        } );
    },

    initGuesses( guesses, eventNo?: string | null ) {
        const resultGuesses = Array.isArray( guesses ) ? guesses : [ guesses ];

        set( ( draft ) => {
            resultGuesses.forEach( ( guess ) => {
                const resolvedEventNo = eventNo || guess.eventNo || draft.currentEventNo;
                registerGuessEvent( draft, resolvedEventNo, guess.guessNo );
                applyMarketPriceUpdate( draft, {
                    guessNo: guess.guessNo,
                    yesPrice: +guess.yesRealtimePrice,
                    noPrice: +guess.noRealtimePrice,
                    timestamp: normalizeTimestamp( guess.marketPriceUpdateTime ),
                } );
            } );
        } );
    },
} ) ) ) );
type TradeAmountMessage = {
    eventNo: string;
    guessNo: string;
    tradeAmount: number;
    timestamp: number; // 毫秒
    guessType: GuessType;
};
// 定义 Socket 监听回调
const socketListener = ( type: string, payload: any ) => {
    const store = useEventConnect.getState();
    if ( type === "market_price" ) {
        store.handleMarketPrice( payload as MarketPriceMessage );
    } else if ( type === "order_book_snapshot_v2" ) {
        store.handleOrderBookSnapshotV2( payload as OrderBookSnapshotV2Payload );
    } else if ( type === "order_book_snapshot" ) {
        store.handleOrderbookFullSnapshotMessage( payload as OrderbookFullSnapshotPayload );
    } else if ( type === "trade_amount" ) {
        store.handleTradeAmount( payload as TradeAmountMessage );
    }
};
